/// The week grid of «Послуги → Календар», as pure functions: which seven days
/// a week is, where an instant falls on a club's wall clock, the instant a
/// wall-clock time is, and how the blocks of one day sit side by side.
///
/// Every clock here is the CLUB's (its branch's IANA zone), never the
/// browser's: a manager in Lisbon looking at a Kyiv club sees Kyiv time, the
/// time the client walks in. Dates are 'YYYY-MM-DD' local dates; times of day
/// are minutes after local midnight.

/// 'YYYY-MM-DD' + n days. Calendar arithmetic on the date alone — no clock,
/// so no DST hour can push it onto the wrong day.
export const addDays = (date: string, days: number): string => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

/// 0 = Sunday … 6 = Saturday — the server's `weekday` (specs/services.md §4).
export const weekdayOf = (date: string): number => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

/// The Monday of `date`'s week: a Ukrainian week starts on Monday.
export const weekStart = (date: string): string => addDays(date, -((weekdayOf(date) + 6) % 7))

/// The seven dates of the week that starts on `monday`.
export const weekDays = (monday: string): readonly string[] =>
  Array.from({ length: 7 }, (_, index) => addDays(monday, index))

/// Monday-first weekdays, as the server numbers them.
export const WEEK_ORDER: readonly number[] = [1, 2, 3, 4, 5, 6, 0]

const wallParts = (at: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at)
  const read = (type: string): number => Number(parts.find((part) => part.type === type)?.value ?? '0')
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  }
}

/// How far `timeZone`'s wall clock is ahead of UTC at `at`, in milliseconds.
const offsetAt = (at: Date, timeZone: string): number => {
  const p = wallParts(at, timeZone)
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return wall - Math.floor(at.getTime() / 1000) * 1000
}

/// Where an instant falls on the club's wall: its local date and minutes
/// after local midnight.
export const wallOf = (at: Date, timeZone: string): Readonly<{ date: string; minutes: number }> => {
  const p = wallParts(at, timeZone)
  const pad = (n: number) => String(n).padStart(2, '0')
  return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, minutes: p.hour * 60 + p.minute }
}

/// The instant a wall-clock time is in `timeZone`. Resolved twice, like the
/// server's `localMidnight`: the first guess uses the offset at the UTC
/// reading, which differs on the night the clocks change. A time that does
/// not exist (03:30 on the spring-forward night) lands an hour later — the
/// server refuses it as outside working hours anyway.
export const wallInstant = (date: string, minutes: number, timeZone: string): Date => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const guess = Date.UTC(y, m - 1, d) + minutes * 60_000
  const first = guess - offsetAt(new Date(guess), timeZone)
  return new Date(guess - offsetAt(new Date(first), timeZone))
}

/// `[from, to)` of a week on the club's wall, as instants — what to ask the
/// server for. Both ends are local midnights.
export const weekRange = (monday: string, timeZone: string): Readonly<{ from: Date; to: Date }> => ({
  from: wallInstant(monday, 0, timeZone),
  to: wallInstant(addDays(monday, 7), 0, timeZone),
})

/// 'HH:MM' → minutes; '24:00' is 1440 (an interval to midnight).
export const minutesOf = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number) as [number, number]
  return h * 60 + m
}

/// Minutes → 'HH:MM' (1440 → '24:00').
export const hhmm = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`

/// Which club a cell picked on «Усі клуби» books in: the club where the
/// provider works at that weekday and minute, else where they work that day,
/// else where they work at all, else `fallback` — the server checks working
/// hours per club, so the first club in the list is the wrong default for
/// someone who works only in the second. Only clubs in `reach` count; an
/// interval with no club (null) works anywhere and decides nothing.
export const branchForSlot = (
  hours: readonly Readonly<{ branchId: string | null; weekday: number; startLocal: string; endLocal: string }>[],
  date: string,
  minutes: number,
  reach: readonly string[],
  fallback: string | null,
): string | null => {
  const mine = hours.filter((h): h is typeof h & { branchId: string } => h.branchId !== null && reach.includes(h.branchId))
  const day = mine.filter((h) => h.weekday === weekdayOf(date))
  const at = day.find((h) => minutes >= minutesOf(h.startLocal) && minutes < minutesOf(h.endLocal))
  return at?.branchId ?? day[0]?.branchId ?? mine[0]?.branchId ?? fallback
}

// ---- the day's blocks ----

/// One thing on a day column: minutes after local midnight. `busyEnd` is the
/// end of the buffer after it (≥ end); the block is drawn to `end` and its
/// buffer hatched to `busyEnd`.
export type DayBlock = Readonly<{ id: string; start: number; end: number; busyEnd: number }>

export type PlacedBlock = DayBlock & Readonly<{ lane: number; lanes: number }>

/// The part of `[startsAt, busyUntil)` that falls on `date`, in minutes —
/// null when none of it does. A session over midnight shows on both days.
export const blockOnDay = (
  item: Readonly<{ id: string; startsAt: string; endsAt: string; busyUntil?: string | null }>,
  date: string,
  timeZone: string,
): DayBlock | null => {
  const dayFrom = wallInstant(date, 0, timeZone).getTime()
  const dayTo = wallInstant(addDays(date, 1), 0, timeZone).getTime()
  const start = Date.parse(item.startsAt)
  const end = Date.parse(item.endsAt)
  const busy = Math.max(end, item.busyUntil ? Date.parse(item.busyUntil) : end)
  if (start >= dayTo || busy <= dayFrom) return null
  // Minutes on the wall, not elapsed: on a 25-hour day the hour repeats.
  const minute = (at: number): number =>
    at <= dayFrom ? 0 : at >= dayTo ? 1440 : wallOf(new Date(at), timeZone).minutes
  const startMin = minute(start)
  const endMin = Math.max(startMin, minute(end))
  return { id: item.id, start: startMin, end: endMin, busyEnd: Math.max(endMin, minute(busy)) }
}

/// Blocks that overlap share the column in lanes, like a desk diary: each
/// cluster of overlapping blocks gets as many lanes as it needs at most, and
/// every block in it takes the first lane free at its start. Buffers count —
/// a block cannot sit in a lane whose previous block's buffer is still on.
export const layoutDay = (blocks: readonly DayBlock[]): readonly PlacedBlock[] => {
  const sorted = [...blocks].sort((a, b) => a.start - b.start || b.busyEnd - a.busyEnd || a.id.localeCompare(b.id))
  // The clusters are this function's own and never escape mutable — copying
  // them per block would make a busy day quadratic (as in payroll's groupBy).
  type Cluster = { end: number; members: { block: DayBlock; lane: number }[]; laneEnds: number[] }
  const clusters = sorted.reduce<Cluster[]>((acc, block) => {
    const last = acc[acc.length - 1]
    const cluster: Cluster =
      last && block.start < last.end ? last : { end: 0, members: [], laneEnds: [] }
    if (cluster !== last) acc.push(cluster)
    const free = cluster.laneEnds.findIndex((laneEnd) => laneEnd <= block.start)
    const lane = free === -1 ? cluster.laneEnds.length : free
    cluster.laneEnds[lane] = Math.max(block.busyEnd, block.start + 1)
    cluster.members.push({ block, lane })
    cluster.end = Math.max(cluster.end, block.busyEnd, block.start + 1)
    return acc
  }, [])
  return clusters.flatMap((cluster) =>
    cluster.members.map(({ block, lane }) => ({ ...block, lane, lanes: cluster.laneEnds.length })),
  )
}

/// The hours the grid shows: 08:00–21:00 at the least, widened to whole hours
/// around anything that falls outside.
export const visibleHours = (
  spans: readonly Readonly<{ start: number; end: number }>[],
  base: Readonly<{ from: number; to: number }> = { from: 8, to: 21 },
): Readonly<{ from: number; to: number }> => ({
  from: Math.min(base.from, ...spans.map((span) => Math.floor(span.start / 60))),
  to: Math.max(base.to, ...spans.map((span) => Math.ceil(span.end / 60))),
})

/// The minute a click (or a keyboard cell) means: snapped DOWN to the grid
/// step, so a click in the lower half of 10:00–10:30 means 10:00 with a
/// 30-minute step.
export const snap = (minutes: number, step: number): number => Math.floor(minutes / step) * step

/// «20–26 жовт. 2026» / «29 вер. – 5 жовт. 2026» — the week in the toolbar.
export const weekLabel = (monday: string): string => {
  const sunday = addDays(monday, 6)
  const fmt = (date: string, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', ...options }).format(new Date(`${date}T00:00:00Z`))
  const year = sunday.slice(0, 4)
  return monday.slice(0, 7) === sunday.slice(0, 7)
    ? `${Number(monday.slice(8))}–${fmt(sunday, { day: 'numeric', month: 'short' })} ${year}`
    : `${fmt(monday, { day: 'numeric', month: 'short' })} – ${fmt(sunday, { day: 'numeric', month: 'short' })} ${year}`
}

/// «ср, 28 жовтня 2026» — the day in the toolbar. Built from parts: the
/// formatter's own «28 жовтня 2026 р.» ends in a year mark the week label has not.
export const dayLabel = (date: string): string => {
  const fmt = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', ...options }).format(new Date(`${date}T00:00:00Z`))
  return `${fmt({ weekday: 'short' })}, ${fmt({ day: 'numeric', month: 'long' })} ${date.slice(0, 4)}`
}

/// The people a day's columns are: everybody who provides a service, or runs
/// a session on that day, once each, by name. A session nobody runs adds a
/// column of its own — `null`, last — so a class without a trainer is never
/// left off the day.
export const dayPeople = (
  providers: readonly string[],
  sessionsOfDay: readonly Readonly<{ trainerUserId: string | null }>[],
  nameOf: (userId: string) => string,
): readonly (string | null)[] => {
  const named = [
    ...new Set([...providers, ...sessionsOfDay.flatMap((s) => (s.trainerUserId ? [s.trainerUserId] : []))]),
  ].sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'uk'))
  return sessionsOfDay.some((s) => s.trainerUserId === null) ? [...named, null] : named
}

/// «Пн 20» — a column's head.
export const dayHead = (date: string): Readonly<{ weekday: string; day: string }> => ({
  weekday: new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', weekday: 'short' }).format(new Date(`${date}T00:00:00Z`)),
  day: String(Number(date.slice(8))),
})

/// The grid's roving cell kept inside the grid: a week or a provider with
/// fewer visible hours must not leave it past the last row, or no cell is
/// tabbable and Tab skips the calendar.
export const clampCell = (
  cell: Readonly<{ day: number; slot: number }>,
  days: number,
  slots: number,
): Readonly<{ day: number; slot: number }> => ({
  day: Math.max(0, Math.min(cell.day, days - 1)),
  slot: Math.max(0, Math.min(cell.slot, slots - 1)),
})

// ---- orientation: the past, the first view, the day's load ----

/// Whether a cell of the grid is in the past: a date before today, or a cell
/// of today that has ENDED. The cell under the red line is still now — a
/// walk-in at 10:03 books the 10:00 slot — so only whole past cells count.
export const isPastCell = (
  date: string,
  minutes: number,
  step: number,
  today: string,
  nowMinutes: number,
): boolean => date < today || (date === today && minutes + step <= nowMinutes)

/// The minute the grid opens on, before it is scrolled to a third from the
/// top: now, when today is on the grid and inside the hours shown; otherwise
/// the earliest thing on it (a week ahead opens on its first booking); null
/// when there is nothing to look at — the grid stays at its first hour.
export const anchorMinute = (
  dates: readonly string[],
  today: string,
  nowMinutes: number,
  starts: readonly number[],
  hours: Readonly<{ from: number; to: number }>,
): number | null => {
  if (dates.includes(today) && nowMinutes >= hours.from * 60 && nowMinutes < hours.to * 60) return nowMinutes
  return starts.length > 0 ? Math.min(...starts) : null
}

/// How many things start on each of `dates` on the club's wall — the quiet
/// «4 записи» under a week's day. A cancelled one does not count: the desk
/// reads the load, and nobody comes to a cancelled session.
export const countByDate = (
  items: readonly Readonly<{ startsAt: string; cancelled: boolean }>[],
  dates: readonly string[],
  timeZone: string,
): ReadonlyMap<string, number> =>
  items.reduce(
    (counts, item) => {
      const on = wallOf(new Date(item.startsAt), timeZone).date
      return item.cancelled || !counts.has(on) ? counts : new Map(counts).set(on, (counts.get(on) ?? 0) + 1)
    },
    new Map(dates.map((date): [string, number] => [date, 0])),
  )

// ---- the blocks themselves ----

/// A column's blocks in two layers: the live ones share lanes as
/// `layoutDay` lays them out, and the ones `behind` (a cancelled session)
/// are laid out among themselves only — drawn under the live ones, they
/// never halve a live block nor take its lane. Behind blocks come first,
/// so they are drawn (and tabbed) before what covers them.
export const layoutLayers = (
  blocks: readonly DayBlock[],
  behind: (id: string) => boolean,
): readonly (PlacedBlock & Readonly<{ behind: boolean }>)[] => [
  ...layoutDay(blocks.filter((block) => behind(block.id))).map((block) => ({ ...block, behind: true })),
  ...layoutDay(blocks.filter((block) => !behind(block.id))).map((block) => ({ ...block, behind: false })),
]

/// Whether a block on `date` is over: a day before today, or today's that
/// ended at or before now. A block still running is not — the desk is in it.
export const isPastBlock = (date: string, end: number, today: string, nowMinutes: number): boolean =>
  date < today || (date === today && end <= nowMinutes)

/// A group session's places in a block's corner: «3/10» once the number
/// booked is known, the capacity alone («10») until it is.
export const placesMeta = (capacity: number, booked?: number | undefined): string =>
  booked === undefined ? String(capacity) : `${booked}/${capacity}`

/// Where a behind block's chip goes, in minutes: at its start, or — when a
/// live block over the chip's corner covers that (a freed slot booked again)
/// — just below whatever covers it, while that is still inside the block.
/// `lifted` when no room inside is free: the chip then sits at the block's
/// end, drawn over the live ones, so a cancelled session is never out of reach.
export const chipSpot = (
  block: PlacedBlock,
  live: readonly PlacedBlock[],
  chipMinutes: number,
): Readonly<{ at: number; lifted: boolean }> => {
  // The chip's left edge; a live block covers it when its lane spans that.
  const x = block.lane / block.lanes
  const over = live.filter((other) => other.lane / other.lanes <= x && x < (other.lane + 1) / other.lanes)
  const below = (at: number): number => {
    const cover = over.find((other) => other.start < at + chipMinutes && at < other.busyEnd)
    return cover ? below(cover.busyEnd) : at
  }
  const at = below(block.start)
  return at + chipMinutes <= block.end
    ? { at, lifted: false }
    : { at: Math.max(block.start, block.end - chipMinutes), lifted: true }
}
